"use strict";
/**
 * sRPE — FORMULE CANONICHE (@leopart/calc).
 *
 * `rpeValue` nel DB è GIÀ il prodotto RPE × minuti (calcolato al save dal
 * write-path di eventresults): la semantica canonica lo usa così com'è.
 * Il censimento 2026-07-26 (vault/Architettura/Calcoli e formule.md, danno
 * attivo #2) ha trovato 2 semantiche divergenti (Σ rpeValue vs value×duration
 * ri-moltiplicato): questa è LA fonte unica, le varianti sono state ritirate.
 *
 * Il bucketing per giorno NON vive qui: il chiamante passa `date` già risolte
 * al giorno logico (la timezone — Europe/Rome, pattern EFFORT_TZ — resta
 * responsabilità del backend).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.sessionSrpe = sessionSrpe;
exports.clampSessionDuration = clampSessionDuration;
exports.dailySrpeMap = dailySrpeMap;
exports.rpeDaIndiceSforzo = rpeDaIndiceSforzo;
const constants_1 = require("./constants");
/**
 * sRPE di sessione = RPE (1-10) × minuti effettivi (Foster), ARROTONDATO
 * all'intero.
 *
 * DECISIONE PRODOTTO 2026-07-29 (approvata da Federico): l'AU (arbitrary
 * unit) è un'unità arbitraria — i decimali NON sono precisione, sono rumore:
 * 318.5 AU e 319 AU descrivono lo stesso carico. Il valore PERSISTITO
 * (`eventResults.rpeValue`, `sessions.internalLoad.srpe`) è quindi sempre
 * intero, così somme giornaliere, medie ACWR e heatmap non trascinano code
 * decimali nate da un input frazionario (RPE 7 × 45.5 min → 319, non 318.5).
 *
 * Cambio di semantica ⇒ registry 'srpe' bump a version 2 (trigger del
 * ricalcolo massivo P2 sugli output persistiti).
 *
 * Arrotondamento: `Math.round` (half-up sui positivi — sRPE è sempre > 0).
 * Chi ricalcola lato Mongo NON deve usare `$round` (banker's rounding, .5 →
 * pari): l'equivalente è `$floor: { $add: [prodotto, 0.5] }`.
 */
function sessionSrpe(rpe, minutes) {
    return Math.round(rpe * minutes);
}
/**
 * Riporta una durata di sessione dentro il dominio valido (G7, 2026-07-30).
 *
 * Serve a chi ricava i minuti da un orologio anziché da un input esplicito:
 * `completeWorkoutLog` calcola `now - startedAt`, quindi un atleta che apre la
 * scheda e la chiude il giorno dopo produce durate da ~1500 minuti (25 ore) e
 * sRPE da 11920 AU, che poi dominano ACWR, monotonia e strain di quell'atleta.
 * In produzione è successo: 4 record reali, tutti dello stesso atleta.
 *
 * Il flusso eventi era già protetto (`@Min(1) @Max(600)` sul DTO); questo porta
 * il flusso programmazione allo STESSO dominio, dalla stessa fonte.
 *
 * Politica: si CLAMPA, non si scarta — la sessione è avvenuta davvero e il suo
 * carico va contato; è la misura dell'orologio a non essere attendibile. Il
 * chiamante riceve `clamped` per poterlo tracciare (lineage/log) invece di
 * correggere in silenzio.
 */
function clampSessionDuration(minutes) {
    if (!Number.isFinite(minutes) || minutes < constants_1.DURATION_MIN_MINUTES) {
        return { minutes: constants_1.DURATION_MIN_MINUTES, clamped: true };
    }
    if (minutes > constants_1.DURATION_MAX_MINUTES) {
        return { minutes: constants_1.DURATION_MAX_MINUTES, clamped: true };
    }
    return { minutes, clamped: false };
}
/**
 * sRPE del singolo record secondo la SEMANTICA CANONICA:
 *  1. `rpeValue` numerico finito → usato così com'è (è già RPE × minuti);
 *  2. fallback SOLO per record legacy senza rpeValue: `value × duration`
 *     (entrambi numerici finiti);
 *  3. altrimenti il record non ha contributo valido → null (ignorato,
 *     NESSUNA entry a 0 nella mappa).
 *
 * NB: il fallback (2) NON passa da `sessionSrpe` di proposito — è read-path,
 * deve riprodurre il contributo del record COM'È, non "correggerlo" con
 * l'arrotondamento v2 su dati che il ricalcolo P2 non ha ancora toccato.
 * Dopo un `recompute srpe --apply` questi record hanno un rpeValue intero e
 * il ramo (2) non viene più imboccato. Semantica di `daily-srpe` INVARIATA.
 */
function recordSrpe(r) {
    if (typeof r.rpeValue === 'number' && Number.isFinite(r.rpeValue)) {
        return r.rpeValue;
    }
    if (typeof r.value === 'number' &&
        Number.isFinite(r.value) &&
        typeof r.duration === 'number' &&
        Number.isFinite(r.duration)) {
        return r.value * r.duration;
    }
    return null;
}
/**
 * Aggrega i risultati RPE in una mappa giornaliera di sRPE
 * (chiave = YYYY-MM-DD, valore = Σ sRPE del giorno).
 *
 * Record `skipped` (enum stringa presente) ESCLUSI; record senza contributo
 * valido ignorati (nessuna entry). Semantica per-record in `recordSrpe`.
 */
function dailySrpeMap(results) {
    var _a;
    const daily = new Map();
    for (const r of results) {
        if (r.skipped != null)
            continue; // skipped esclusi (campo assente = valido)
        const srpe = recordSrpe(r);
        if (srpe === null)
            continue;
        daily.set(r.date, ((_a = daily.get(r.date)) !== null && _a !== void 0 ? _a : 0) + srpe);
    }
    return daily;
}
/**
 * Indice di sforzo 0-100 → RPE sulla sua scala originale 1-10.
 *
 * Il backend pubblica `effortIndex` come interpolazione lineare della media
 * RPE del periodo su 0-100 (`teams.service.ts`, campo `effortIndex`). È una
 * comodità di calcolo, non un'unità di misura: l'RPE è una scala 1-10 e "70%
 * di sforzo" non vuol dire niente per chi legge — nessuno chiede a un atleta
 * di allenarsi al settanta per cento di RPE.
 *
 * Questa è l'inversa esatta di quell'interpolazione, e va applicata ovunque
 * il valore venga MOSTRATO. Il web la usa nella colonna Sforzo del roster,
 * il mobile nelle schede readiness.
 *
 * `null` in ingresso (nessun RPE nel periodo) resta `null`: sforzo non
 * pervenuto non è sforzo nullo.
 *
 * Attenzione allo zero: indice 0 NON è "nessun dato", è RPE 1 — il minimo
 * della scala, cioè un allenamento davvero leggero. L'assenza il backend la
 * manda già come `null`. Trattare lo 0 come vuoto (come faceva il web)
 * nasconde proprio l'atleta che si è allenato pochissimo.
 */
function rpeDaIndiceSforzo(indice) {
    if (indice === null || !Number.isFinite(indice) || indice < 0)
        return null;
    return (indice * (constants_1.RPE_MAX - constants_1.RPE_MIN)) / 100 + constants_1.RPE_MIN;
}
